import { NavLink, Link } from 'react-router-dom';
import { IconCalendar, IconClock, IconGift, IconImages, IconMoon, IconPin, IconSun } from './Icon';
import { initials, useSession } from '../lib/session';
import { toggleTheme, useTheme } from '../lib/theme';

/** `tour` names the tab for the first-visit tour (D68), which points at it. */
const DESTS: Array<{ to: string; label: string; Icon: typeof IconClock; tour?: string }> = [
  { to: '/now', label: 'Now', Icon: IconClock },
  { to: '/schedule', label: 'Schedule', Icon: IconCalendar, tour: 'schedule' },
  { to: '/map', label: 'Map', Icon: IconPin, tour: 'map' },
  { to: '/gallery', label: 'Photos', Icon: IconImages, tour: 'photos' },
  { to: '/gifts', label: 'Gifts', Icon: IconGift },
];

/**
 * Sticky header: monogram, destinations, theme toggle, profile (D32).
 *
 * **Five destinations. The fifth slot is spent, and the sixth does not
 * exist.** The ceiling is measured rather than assumed: five labelled tabs
 * clear the monogram and the avatar by 1.1px a side at 375px — they overflow
 * their own flex box into the header's gutter, which is why nothing visibly
 * breaks and why there is no more room. A sixth wants 43px more than exists at
 * 390px and 9px more at 430px, the widest common phone; fitting six means
 * 36.8px tabs, under the 44px touch target.
 *
 * Gifts took the empty slot on request (D63, amended). Three features were
 * refused it before that — D54 the scorecard, D57 the playlist, D62 the
 * icebreakers — and each went to a surface inside a page instead. Those
 * refusals still stand on the arithmetic above; what changed is that the slot
 * is now full rather than held in reserve, so the next request is a swap
 * rather than an addition.
 *
 * The icebreakers briefly held this slot and gave it back, and have since been
 * removed altogether (D69). Credits is still in the footer, which is where it
 * belongs regardless.
 *
 * "Gifts" is five characters, shorter than every label here except "Now" and
 * "Map", which is why the 1.1px measurement above survives it unchanged.
 *
 * The 375px breakpoint drops the labels below that width and keeps the icons.
 */
export default function Header() {
  const session = useSession();
  const { resolved } = useTheme();

  return (
    <header className="hdr">
      <Link className="hdr-brand" to="/now" aria-label="Meg and Videet — home">
        <span className="hdr-brand-short">M&amp;V</span>
      </Link>
      <nav className="hdr-dests" aria-label="Sections">
        {DESTS.map(({ to, label, Icon, tour }) => (
          <NavLink key={to} to={to} className="hdr-dest" data-tour={tour}>
            <Icon />
            <span>{label}</span>
          </NavLink>
        ))}
      </nav>
      <div className="hdr-right">
        <button
          type="button"
          className="hdr-icon-btn"
          onClick={toggleTheme}
          aria-label={resolved === 'dark' ? 'Switch to light' : 'Switch to dark'}
        >
          {resolved === 'dark' ? <IconMoon /> : <IconSun />}
        </button>
        <Link className="avatar" to="/me" aria-label="Your profile">
          {session ? initials(session.name) : '?'}
        </Link>
      </div>
    </header>
  );
}
